import dot from 'dot-prop-immutable';
import { createSelector } from 'reselect';

import {
  type ExpiredSharedTechnicalDataPackage,
  type PartFileViewerPageData,
  type PartViewerPageData,
  PROCESSING_CATEGORIES,
  type ProcessingStatuses,
  type SharedTechnicalDataPackage,
  type TechnicalDataPackage,
} from '@frontend/api';

import { VIEWER_MODES, type ViewerMode } from 'constants/all';

import { LOCATION_CHANGE } from 'store/app/actions';
import type { RootState } from 'store/configureStore';
import {
  getIsMaterialExcluded,
  getMaterialById,
  getSupplierMaterialsByMaterialId,
} from 'store/materials/reducer';

import { isStatusInProgress } from 'utils/partUtils';

import {
  types,
  type MultiLevelBom,
  type PartFileInterrogationStatuses,
  type PartFileProcessingStatuses,
  type ViewerPageAction,
} from './actions';
import { APPS_PANEL_STATES, PART_PANEL_STATES } from './constants';
import type { AppsPanelStateValues, PartPanelStateValues } from './constants';
import { getShouldHideFile } from './utils';

const IS_APPS_PANEL_COLLAPSED = 'partviewer:isAppsPanelCollapsed';

export type { PartFileInterrogationStatuses, PartFileProcessingStatuses };

/* Unloaded state is the initial `{}`, not an API response. `null` would narrow
 * more clearly, but `getIsLoading` is `Object.keys(package).length === 0`
 * (`Object.keys(null)` throws), and a few readers spread the selected package
 * or read fields on it while it can still be empty (`watchUpdatePartMaster`
 * reads `partMasterUuid` directly). Keep `{}` until those call sites are guarded.
 */
type StoredTechnicalDataPackage =
  | TechnicalDataPackage
  | SharedTechnicalDataPackage
  | ExpiredSharedTechnicalDataPackage
  | Record<string, never>;

/* Stand-in for "not the empty object." Every loaded package has `partMasterUuid`
 * via `TechnicalDataPackageBase`; `{}` does not. This is not a variant check —
 * owned, active share, and expired share are separated below by their own fields.
 */
const isLoadedTechnicalDataPackage = (
  technicalDataPackage: StoredTechnicalDataPackage,
): technicalDataPackage is
  | TechnicalDataPackage
  | SharedTechnicalDataPackage
  | ExpiredSharedTechnicalDataPackage =>
  'partMasterUuid' in technicalDataPackage;

const isOwnedTechnicalDataPackage = (
  technicalDataPackage: StoredTechnicalDataPackage,
): technicalDataPackage is TechnicalDataPackage =>
  'isModifiable' in technicalDataPackage;

const isSharedTechnicalDataPackage = (
  technicalDataPackage: StoredTechnicalDataPackage,
): technicalDataPackage is SharedTechnicalDataPackage =>
  'isExpired' in technicalDataPackage && !technicalDataPackage.isExpired;

const isExpiredSharedTechnicalDataPackage = (
  technicalDataPackage: StoredTechnicalDataPackage,
): technicalDataPackage is ExpiredSharedTechnicalDataPackage =>
  'expiredAt' in technicalDataPackage;

const hasPartInformation = (
  technicalDataPackage: StoredTechnicalDataPackage,
): technicalDataPackage is TechnicalDataPackage | SharedTechnicalDataPackage =>
  'partInformation' in technicalDataPackage;

type ViewerPageState = {
  activeFileUUID: string | null;
  activePartUUID: string | null;
  appsPanelState: AppsPanelStateValues;
  appsPanelMeta: Record<string, unknown> | null;
  areSupportingFilesUploading: boolean;
  isAppsPanelCollapsed: boolean;
  isEditingPartDetails: boolean;
  isPartDetailsExpanded: boolean;
  isPartDetailsSaving: boolean;
  isSupportingFileDeleting: boolean;
  isTechnicalDataPackageDownloading: boolean;
  mode: ViewerMode | null;
  multiLevelBom: MultiLevelBom;
  partFileInterrogationStatuses: PartFileInterrogationStatuses;
  partFileProcessingStatuses: PartFileProcessingStatuses;
  partPanelState: PartPanelStateValues;
  technicalDataPackage: StoredTechnicalDataPackage;
};

const initialState = (): ViewerPageState => ({
  activeFileUUID: null,
  activePartUUID: null,
  appsPanelState: APPS_PANEL_STATES.VIEWER_CHAT,
  appsPanelMeta: null,
  areSupportingFilesUploading: false,
  isAppsPanelCollapsed:
    localStorage.getItem(IS_APPS_PANEL_COLLAPSED) === 'true',
  isEditingPartDetails: false,
  isPartDetailsExpanded: false,
  isPartDetailsSaving: false,
  isSupportingFileDeleting: false,
  isTechnicalDataPackageDownloading: false,
  mode: null,
  multiLevelBom: {},
  partFileInterrogationStatuses: {},
  partFileProcessingStatuses: {},
  partPanelState: PART_PANEL_STATES.CAD,
  technicalDataPackage: {},
});

const reducer = (
  state = initialState(),
  action: ViewerPageAction,
): ViewerPageState => {
  switch (action.type) {
    case LOCATION_CHANGE:
      return {
        ...initialState(),
      };
    case types.SET_ACTIVE_FILE:
      return {
        ...state,
        activeFileUUID: action.payload.file.uuid,
        activePartUUID: action.payload.file.partUuid ?? state.activePartUUID,
      };
    case types.RECEIVE_PART_FILE_INTERROGATION_STATUSES:
      return dot.merge(
        state,
        'partFileInterrogationStatuses',
        action.payload.interrogationStatuses,
      );
    case types.RECEIVE_PART_FILE_PROCESSING_STATUSES:
      return dot.merge(
        state,
        'partFileProcessingStatuses',
        action.payload.processingStatuses,
      );
    case types.RECEIVE_MULTI_LEVEL_BOM:
      return {
        ...state,
        multiLevelBom: action.payload.multiLevelBom,
      };
    case types.RECEIVE_TECHNICAL_DATA_PACKAGE: {
      const { technicalDataPackage } = action.payload;
      return {
        ...state,
        activePartUUID: state.activePartUUID
          ? state.activePartUUID
          : 'activePartUuid' in technicalDataPackage
          ? technicalDataPackage.activePartUuid
          : null,
        technicalDataPackage,
      };
    }
    case types.REMOVE_SUPPORTING_FILE_SUCCESS: {
      const { technicalDataPackage } = state;
      if (!hasPartInformation(technicalDataPackage)) {
        return state;
      }
      const removedFileUUID = action.payload.supportingFileUUID;
      const nextPackage = {
        ...technicalDataPackage,
        partInformation: technicalDataPackage.partInformation.map((part) => {
          if (part.uuid === action.payload.partUUID) {
            return {
              ...part,
              partFiles: part.partFiles.filter(
                (partFile) => partFile.uuid != removedFileUUID,
              ),
            };
          } else {
            return part;
          }
        }),
      };
      return {
        ...state,
        technicalDataPackage: nextPackage,
      };
    }
    case types.PART_DETAILS_TOGGLE_COLLAPSE:
      return {
        ...state,
        isPartDetailsExpanded: !state.isPartDetailsExpanded,
      };
    case types.PART_DETAILS_TOGGLE_EDITABLE:
      return {
        ...state,
        isEditingPartDetails: !state.isEditingPartDetails,
      };
    case types.PART_DETAILS_UPDATE_ERROR:
      return {
        ...state,
        isPartDetailsSaving: false,
      };
    case types.PART_DESCRIPTORS_UPDATE_REQUEST:
      return {
        ...state,
        isPartDetailsSaving: true,
      };
    case types.PART_DETAILS_UPDATE_SUCCESS:
      return {
        ...state,
        isEditingPartDetails: false,
        isPartDetailsSaving: false,
        technicalDataPackage: action.payload.technicalDataPackage,
      };
    case types.PART_MASTER_DETAILS_UPDATE_SUCCESS:
      return {
        ...state,
        technicalDataPackage: action.payload.technicalDataPackage,
      };
    case types.UPLOAD_SUPPORTING_FILES_RECEIVE_FILES: {
      const { technicalDataPackage } = state;
      if (!hasPartInformation(technicalDataPackage)) {
        return state;
      }
      const nextPackage = {
        ...technicalDataPackage,
        partInformation: technicalDataPackage.partInformation.map((part) => {
          if (part.uuid === action.payload.uuid) {
            const existingIndex = part.partFiles.findIndex(
              (file) => file.uuid === action.payload.supportingFile.uuid,
            );
            let nextPartFiles;
            if (existingIndex !== -1) {
              // Update the existing entry
              nextPartFiles = part.partFiles.map((file, idx) =>
                idx === existingIndex ? action.payload.supportingFile : file,
              );
            } else {
              // Concat as new entry
              nextPartFiles = part.partFiles.concat([
                action.payload.supportingFile,
              ]);
            }
            return {
              ...part,
              partFiles: nextPartFiles,
            };
          } else {
            return part;
          }
        }),
      };
      return {
        ...state,
        technicalDataPackage: nextPackage,
      };
    }
    case types.UPLOAD_SUPPORTING_FILES_REQUEST:
      return {
        ...state,
        areSupportingFilesUploading: true,
      };
    case types.UPLOAD_SUPPORTING_FILES_ERROR:
    case types.UPLOAD_SUPPORTING_FILES_SUCCESS:
      return {
        ...state,
        areSupportingFilesUploading: false,
      };
    case types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_REQUEST:
      return {
        ...state,
        isTechnicalDataPackageDownloading: true,
      };
    case types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_ERROR:
    case types.NOTIFY_PACKAGE_TOO_LARGE:
      return {
        ...state,
        isTechnicalDataPackageDownloading: false,
      };
    case types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_SUCCESS:
      return {
        ...state,
        isTechnicalDataPackageDownloading: false,
      };
    case types.VIEWER_PAGE_CLEAR:
      return {
        ...initialState(),
        //persist the previous state (If it is QPV, the default is the PartSetupTool)
        appsPanelState: state.appsPanelState,
        //persist meta state
        appsPanelMeta: state.appsPanelMeta,
        isAppsPanelCollapsed: state.isAppsPanelCollapsed,
      };
    case types.VIEWER_PAGE_SET_MODE:
      return {
        ...state,
        mode: action.payload.mode,
      };
    case types.SET_APPS_PANEL_STATE:
      return {
        ...state,
        appsPanelState: action.payload.appsPanelState,
        appsPanelMeta: action.payload.meta || null,
      };
    case types.SET_IS_APPS_PANEL_COLLAPSED: {
      localStorage.setItem(
        IS_APPS_PANEL_COLLAPSED,
        String(action.payload.isCollapsed),
      );
      return {
        ...state,
        isAppsPanelCollapsed: action.payload.isCollapsed,
      };
    }
    case types.SET_PART_PANEL_STATE:
      return {
        ...state,
        partPanelState: action.payload.partPanelState,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): ViewerPageState =>
  state.modules.viewerPage.app.page;

export const areSupportingFilesUploading = (state: RootState): boolean =>
  getScope(state).areSupportingFilesUploading;

export const getIsTechnicalDataPackageDownloading = (
  state: RootState,
): boolean => getScope(state).isTechnicalDataPackageDownloading;

export const doesUserOwnPart = (state: RootState): boolean =>
  getScope(state).mode === VIEWER_MODES.OWNER;

export const getActiveFileUUID = (state: RootState): string | null =>
  getScope(state).activeFileUUID;

export const getActivePartUUID = (state: RootState): string | null =>
  getScope(state).activePartUUID;

export const getMultiLevelBom = (state: RootState): MultiLevelBom =>
  getScope(state).multiLevelBom;

export const getTechnicalDataPackage = (
  state: RootState,
): StoredTechnicalDataPackage => getScope(state).technicalDataPackage;

export const isEditingPartDetails = (state: RootState): boolean =>
  getScope(state).isEditingPartDetails;

export const isPartDetailsSaving = (state: RootState): boolean =>
  getScope(state).isPartDetailsSaving;

export const getIsPartDetailsExpanded = (state: RootState): boolean =>
  getScope(state).isPartDetailsExpanded;

export const getPartInformation = (state: RootState): PartViewerPageData[] => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!hasPartInformation(technicalDataPackage)) {
    return [];
  }
  return technicalDataPackage.partInformation;
};

export const getPartByUUID = (
  state: RootState,
  partUUID: string,
): PartViewerPageData | undefined => {
  const partInformation = getPartInformation(state);
  return partInformation?.find((part) => part.uuid === partUUID);
};

export const getActivePart = (
  state: RootState,
): PartViewerPageData | undefined =>
  getPartByUUID(state, getActivePartUUID(state) || '');

// the main file is the primary file of the active part
export const getMainFile = createSelector(
  getActivePart,
  (
    activePart: PartViewerPageData | undefined,
  ): PartFileViewerPageData | undefined => {
    return activePart?.partFiles?.find((partFile) => partFile.isPrimaryFile);
  },
);

export const getPartFileInterrogationStatuses = (
  state: RootState,
): PartFileInterrogationStatuses =>
  getScope(state).partFileInterrogationStatuses;

export const getPartFileProcessingStatuses = (
  state: RootState,
): PartFileProcessingStatuses => getScope(state).partFileProcessingStatuses;

const findPartFile = (
  partFiles: PartFileViewerPageData[] | undefined,
  partFileUuid: string,
): PartFileViewerPageData | undefined => {
  for (const partFile of partFiles ?? []) {
    if (partFile.uuid === partFileUuid) {
      return partFile;
    }
    const found = findPartFile(partFile.assemblyChildFiles, partFileUuid);
    if (found) {
      return found;
    }
  }
  return undefined;
};

// finds a given part file in the part information
const findPartFileInPartInformation = (
  partInformation: PartViewerPageData[] | undefined,
  partFileUuid: string,
): PartFileViewerPageData | undefined => {
  for (const { partFiles } of partInformation ?? []) {
    const found = findPartFile(partFiles, partFileUuid);
    if (found) {
      return found;
    }
  }
  return undefined;
};

export const getPartFileByUUID = (
  state: RootState,
  partFileUuid: string,
): PartFileViewerPageData | undefined => {
  const partInformation = getPartInformation(state);
  return findPartFileInPartInformation(partInformation, partFileUuid);
};

export const getRelatedOrders = (state: RootState) => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isOwnedTechnicalDataPackage(technicalDataPackage)) {
    return [];
  }
  return technicalDataPackage.relatedOrders;
};

export const getRelatedQuotes = (state: RootState) => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isOwnedTechnicalDataPackage(technicalDataPackage)) {
    return [];
  }
  return technicalDataPackage.relatedQuotes;
};

export const getActivePartFile = createSelector(
  getPartInformation,
  getActiveFileUUID,
  (
    partInformation: PartViewerPageData[],
    activePartFileUuid: string | null,
  ): PartFileViewerPageData | undefined => {
    if (!activePartFileUuid) return undefined;
    return findPartFileInPartInformation(partInformation, activePartFileUuid);
  },
);

export const getIsActivePartFileAnAssembly = (
  state: RootState,
): boolean | undefined => getActivePartFile(state)?.isAssembly;

export const getIsAppsPanelCollapsed = (state: RootState): boolean =>
  getScope(state).isAppsPanelCollapsed;

export const isActivePartFileInterrogating = (state: RootState): boolean => {
  const activePartFileUUID = getActiveFileUUID(state);
  if (!activePartFileUUID) return false;
  const partFileInterrogationStatuses = getPartFileInterrogationStatuses(state);
  const statusInfo = partFileInterrogationStatuses[activePartFileUUID];
  return statusInfo && isStatusInProgress(statusInfo.status);
};

export const getViewerMode = (state: RootState): ViewerMode | null =>
  getScope(state).mode;

export const getPartFileProcessingStatus = (
  state: RootState,
  partFileUuid: string,
): ProcessingStatuses | undefined =>
  getPartFileProcessingStatuses(state)[partFileUuid];

export const getGeometricProcessingFiles = createSelector(
  getPartFileProcessingStatuses,
  (partFileProcessingStatuses: PartFileProcessingStatuses): string[] => {
    const processingUuids: string[] = [];
    for (const [partFileUuid, entry] of Object.entries(
      partFileProcessingStatuses,
    )) {
      const isProcessing = isStatusInProgress(
        entry[PROCESSING_CATEGORIES.PROCESSING]?.status,
      );
      const isViewerCreating = isStatusInProgress(
        entry[PROCESSING_CATEGORIES.VIEWER_CREATION]?.status,
      );
      if (isProcessing || isViewerCreating) {
        processingUuids.push(partFileUuid);
      }
    }
    return processingUuids;
  },
);

export const isActivePartFileProcessing = createSelector(
  getActiveFileUUID,
  getGeometricProcessingFiles,
  (
    activePartFileUuid: string | null,
    processingPartFileUuids: string[],
  ): boolean => {
    if (!activePartFileUuid) return false;
    return processingPartFileUuids.includes(activePartFileUuid);
  },
);

export const isPartFileProcessing = (
  state: RootState,
  partFileUuid: string,
): boolean => {
  const statusInfo = getPartFileProcessingStatus(state, partFileUuid);
  if (statusInfo) {
    const isProcessing = isStatusInProgress(
      statusInfo[PROCESSING_CATEGORIES.PROCESSING]?.status,
    );
    const isViewerCreating = isStatusInProgress(
      statusInfo[PROCESSING_CATEGORIES.VIEWER_CREATION]?.status,
    );
    const isThumbnailCreating = isStatusInProgress(
      statusInfo[PROCESSING_CATEGORIES.THUMBNAIL_CREATION]?.status,
    );
    return isProcessing || isViewerCreating || isThumbnailCreating;
  } else {
    return false;
  }
};

export const getPrimaryFile = (
  state: RootState,
): PartFileViewerPageData | undefined => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isLoadedTechnicalDataPackage(technicalDataPackage)) return undefined;
  const { rootUuid } = technicalDataPackage;
  const rootPart = getPartByUUID(state, rootUuid);
  if (!rootPart) {
    return undefined;
  }
  const primaryFile = rootPart.partFiles.find((pf) => pf.isPrimaryFile);
  return primaryFile;
};

export const getIsTechDataPackageViewerReady = (state: RootState): boolean => {
  try {
    // the viewer is considered ready if the primary file isn't in the tech data package
    const primaryFile = getPrimaryFile(state);
    if (!primaryFile) {
      return true;
    }
    const statusInfo = getPartFileProcessingStatus(state, primaryFile.uuid);
    if (statusInfo) {
      const isProcessing = isStatusInProgress(
        statusInfo[PROCESSING_CATEGORIES.PROCESSING]?.status,
      );
      const isViewerCreating = isStatusInProgress(
        statusInfo[PROCESSING_CATEGORIES.VIEWER_CREATION]?.status,
      );
      return !(isProcessing || isViewerCreating);
    } else {
      return false;
    }
  } catch {
    return false;
  }
};

export const isTechDataPackageExpired = (state: RootState): boolean =>
  isExpiredSharedTechnicalDataPackage(getTechnicalDataPackage(state));

export const getTechDataPackageExpiredAt = (
  state: RootState,
): string | null => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isExpiredSharedTechnicalDataPackage(technicalDataPackage)) {
    return null;
  }
  return technicalDataPackage.expiredAt || null;
};

export const arePartFilesReadyForDownload = (state: RootState): boolean => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!hasPartInformation(technicalDataPackage)) {
    return false;
  }
  const { partInformation } = technicalDataPackage;
  for (const part of partInformation) {
    for (const partFile of part.partFiles.filter(
      (pf) => !pf.isGeneratedByPaperless,
    )) {
      const statusInfo = partFile.status?.processing;
      const canDownload = !isStatusInProgress(
        statusInfo?.[PROCESSING_CATEGORIES.PROCESSING],
      );
      if (!canDownload) {
        return false;
      }
    }
  }
  return true;
};

export const getActiveSupportingFiles = createSelector(
  getActivePart,
  (activePart: PartViewerPageData | undefined): PartFileViewerPageData[] => {
    return ((activePart || {}).partFiles || []).filter(
      (partFile) =>
        !partFile.isPrimaryFile && !partFile.isDerivedFromInterrogation,
    );
  },
);

export const getActivePartPartFiles = createSelector(
  getActivePart,
  (activePart: PartViewerPageData | undefined): PartFileViewerPageData[] => {
    return (activePart || {}).partFiles || [];
  },
);

export const getFilesCountForActivePart = (state: RootState): number => {
  const activePart = getActivePart(state);
  if (!activePart) return 0;
  return (activePart?.partFiles || []).filter(
    (partFile) => !getShouldHideFile(activePart, partFile),
  ).length;
};

export const getCadFileCountForActivePart = createSelector(
  getActivePart,
  (activePart: PartViewerPageData | undefined): number => {
    return ((activePart || {}).partFiles || []).filter(
      (partFile) =>
        partFile.hasGeometry && !partFile.isDerivedFromInterrogation,
    ).length;
  },
);

export const getActiveMaterialId = (state: RootState): number | null => {
  const part = getActivePart(state);
  const materialId = (part || {}).materialId;
  const isMaterialExcluded = getIsMaterialExcluded(state, materialId);
  return isMaterialExcluded ? null : materialId ?? null;
};

export const getActiveMaterialName = (state: RootState): string | null => {
  const part = getActivePart(state);
  const materialId = (part || {}).materialId;
  if (materialId) {
    const supplierMaterials = getSupplierMaterialsByMaterialId(
      state,
      materialId,
    );
    if (supplierMaterials && supplierMaterials.length === 1)
      return supplierMaterials[0].displayName;
    const material = getMaterialById(state, materialId);
    return (material || {}).name;
  } else {
    return null;
  }
};

export const getRootPart = (
  state: RootState,
): PartViewerPageData | undefined => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isLoadedTechnicalDataPackage(technicalDataPackage)) return undefined;
  return getPartByUUID(state, technicalDataPackage.rootUuid);
};

export const getRootPartPrimaryFile = (
  state: RootState,
): PartFileViewerPageData | undefined => {
  const rootPart = getRootPart(state);
  return ((rootPart || {}).partFiles || []).find(
    (partFile) => partFile.isPrimaryFile,
  );
};

export const getRootPartPrimaryFileUuid = (
  state: RootState,
): string | undefined => {
  return getRootPartPrimaryFile(state)?.uuid;
};

export const getActivePartsRootPrimaryFileUuid = createSelector(
  getActivePart,
  (activePart: PartViewerPageData | undefined): string | undefined => {
    if (activePart) {
      const primaryPartFile = activePart.partFiles.find(
        (partFile) => partFile.uuid === activePart.primaryFileUuid,
      );
      if (primaryPartFile?.assemblyParentFileUuid) {
        // the part file has a root part
        return primaryPartFile.assemblyParentFileUuid;
      } else {
        return primaryPartFile?.uuid;
      }
    }
    return undefined;
  },
);

export const getAppsPanelState = (state: RootState): AppsPanelStateValues =>
  getScope(state).appsPanelState;

export const getAppsPanelMeta = (
  state: RootState,
): Record<string, unknown> | null => getScope(state).appsPanelMeta;

export const getPartPanelState = (state: RootState): PartPanelStateValues =>
  getScope(state).partPanelState;

export const getIsExportControlled = (state: RootState): boolean => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (
    !isOwnedTechnicalDataPackage(technicalDataPackage) &&
    !isSharedTechnicalDataPackage(technicalDataPackage)
  ) {
    return false;
  }
  return technicalDataPackage.isExportControlled;
};

export const getIsModifiable = (state: RootState): boolean | undefined => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isOwnedTechnicalDataPackage(technicalDataPackage)) {
    return undefined;
  }
  return technicalDataPackage.isModifiable;
};

export const getIsInAFinalizedQuote = (
  state: RootState,
): boolean | undefined => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isOwnedTechnicalDataPackage(technicalDataPackage)) {
    return undefined;
  }
  return technicalDataPackage.isInAFinalizedQuote;
};

export const getIsInATrashedQuote = (state: RootState): boolean | undefined => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isOwnedTechnicalDataPackage(technicalDataPackage)) {
    return undefined;
  }
  return technicalDataPackage.isInATrashedQuote;
};

export const getPartMasterUuid = (state: RootState): string | undefined => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isLoadedTechnicalDataPackage(technicalDataPackage)) {
    return undefined;
  }
  return technicalDataPackage.partMasterUuid;
};

export const getPartMasterVersion = (state: RootState): number | undefined => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isLoadedTechnicalDataPackage(technicalDataPackage)) {
    return undefined;
  }
  return technicalDataPackage.version;
};

export const getRootPartUuid = (state: RootState): string | undefined => {
  const technicalDataPackage = getTechnicalDataPackage(state);
  if (!isLoadedTechnicalDataPackage(technicalDataPackage)) {
    return undefined;
  }
  return technicalDataPackage.rootUuid;
};

export const getPartUuidFromPartFileUuid = (
  state: RootState,
  partFileUuid: string,
): string | undefined => {
  const partFile = getPartFileByUUID(state, partFileUuid);
  return partFile && partFile.partUuid;
};

export const getIsLoading = (state: RootState): boolean =>
  Object.keys(getTechnicalDataPackage(state)).length === 0;
